import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useShare from '@/src/Share/components/hooks/useShare';
import useSurveyResults from '@/src/Activations/components/hooks/useSurveyResults';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { useEventContext } from '@/src/v2/components/EventContext';
import { dedupeShares } from '@/src/v2/lib/eventStats';
import EventPage from '../chrome/EventPage';
import { IconClose } from '../chrome/icons';
import { ConfirmDialog, Crumb } from '../components/forms';
import { PageHeadActions } from '../components/nav';
import { useToast } from '../components/roster';
import { Segmented, TCard, TEmpty, TFoot, Toolbar, TSkeleton } from '../components/table';
import '../pages-event.css';

/*
 * Shares — the full share ledger for one event: search, sent/unsent
 * filter, thumbnails, per-row delete, client-side CSV exports.
 */

const PAGE = 50;
const PREVIEW_KEY = 'psv2-shares-preview';

type Status = 'all' | 'sent' | 'unsent';

function toCsv(rows: Record<string, any>[]) {
    if (rows.length === 0) return '';
    const keySet = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => keySet.add(k)));
    const keys = Array.from(keySet);
    const esc = (v: any) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return [keys.join(','), ...rows.map((r) => keys.map((k) => esc(r[k])).join(','))].join('\n');
}

function downloadCsv(name: string, rows: Record<string, any>[]) {
    const blob = new Blob([toCsv(rows)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
}

function readPreview(): boolean {
    try {
        return localStorage.getItem(PREVIEW_KEY) !== '0';
    } catch {
        return true;
    }
}

function writePreview(on: boolean) {
    try {
        localStorage.setItem(PREVIEW_KEY, on ? '1' : '0');
    } catch {
        /* storage blocked: preference lasts for this visit only */
    }
}

const createdOf = (s: any) => s.createdAt ?? s.created_at;

function SharesBody() {
    const navigate = useNavigate();
    const { activationId: id } = useEventContext();
    const { getSharesForActivation } = useShare();
    const { getSurveyResults } = useSurveyResults();
    const { orgId } = useUserOrg();
    const authenticatedApi = useAuthenticatedApi();
    const toast = useToast();

    const [shares, setShares] = useState<any[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [surveyRows, setSurveyRows] = useState<any[]>([]);
    const [status, setStatus] = useState<Status>('all');
    const [search, setSearch] = useState('');
    const [preview, setPreview] = useState(readPreview);
    const [limit, setLimit] = useState(PAGE);
    const [pending, setPending] = useState<any | null>(null);
    const [deleting, setDeleting] = useState<Set<number>>(() => new Set());

    const load = useCallback(() => {
        let mounted = true;
        setFailed(false);
        setShares(null);
        (async () => {
            const raw = await getSharesForActivation(id).catch(() => null);
            if (!mounted) return;
            if (raw === null) {
                setFailed(true);
                return;
            }
            setShares(dedupeShares(raw ?? []));
            const sr = await getSurveyResults(id).catch(() => []);
            if (mounted) setSurveyRows(Array.isArray(sr) ? sr : []);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!id) return;
        return load();
    }, [id, load]);

    const all = useMemo(() => shares ?? [], [shares]);
    const sentCount = useMemo(() => all.filter((s) => s.sent).length, [all]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return all
            .filter((s) => (status === 'all' ? true : status === 'sent' ? s.sent : !s.sent))
            .filter((s) => !q || `${s.destination}`.toLowerCase().includes(q))
            .sort((a, b) => moment(createdOf(b)).valueOf() - moment(createdOf(a)).valueOf());
    }, [all, status, search]);

    const removeShare = async (share: any) => {
        if (deleting.has(share.id)) return;
        setDeleting((d) => new Set(d).add(share.id));
        const before = shares;
        setShares((rows) => (rows ?? []).filter((r) => r.id !== share.id));
        const ok = await authenticatedApi
            .delete(`${orgId}/shares/share/${share.id}`)
            .then(() => true)
            .catch(() => false);
        setDeleting((d) => {
            const next = new Set(d);
            next.delete(share.id);
            return next;
        });
        if (ok) {
            toast.show(`Deleted the share to ${share.destination || 'this guest'}.`);
        } else {
            setShares(before);
            toast.show('Couldn’t delete the share — try again.');
        }
    };

    const exportShares = () =>
        downloadCsv(
            `shares-${id}.csv`,
            all.map((s) => ({
                destination: s.destination,
                mediaUrl: s.originalUri ?? '',
                type: s.type,
                sent: s.sent ? 'yes' : 'no',
                disclaimerAccepted: s.disclaimerAccepted ?? '',
                createdAt: createdOf(s) ?? '',
            })),
        );

    const togglePreview = () =>
        setPreview((p) => {
            writePreview(!p);
            return !p;
        });

    const filteredOut = search.trim() !== '' || status !== 'all';
    const sub =
        shares === null
            ? failed
                ? 'Couldn’t load shares'
                : 'Loading shares…'
            : `${all.length.toLocaleString()} share${all.length === 1 ? '' : 's'} · ${sentCount.toLocaleString()} sent`;

    return (
        <div className="ev-page">
            <Crumb label="Sharing & Delivery" onClick={() => navigate(`/activations/${id}/sharing`)} />
            <PageHeadActions
                title="Shares"
                sub={sub}
                actions={
                    <>
                        {surveyRows.length > 0 && (
                            <button type="button" className="tb-btn" onClick={() => downloadCsv(`survey-${id}.csv`, surveyRows)}>
                                Survey CSV
                            </button>
                        )}
                        <button type="button" className="tb-btn" disabled={all.length === 0} onClick={exportShares}>
                            Shares CSV
                        </button>
                    </>
                }
            />

            <Toolbar
                searchValue={search}
                onSearch={(v) => {
                    setSearch(v);
                    setLimit(PAGE);
                }}
                searchPlaceholder="Search destination"
                filterCount={0}
                inline={
                    <>
                        <Segmented<Status>
                            label="Delivery status"
                            value={status}
                            onChange={(v) => {
                                setStatus(v);
                                setLimit(PAGE);
                            }}
                            options={[
                                { value: 'all', label: `All ${all.length}` },
                                { value: 'sent', label: `Sent ${sentCount}` },
                                { value: 'unsent', label: `Unsent ${all.length - sentCount}` },
                            ]}
                        />
                        <button type="button" className="tb-btn ghost" aria-pressed={preview} onClick={togglePreview}>
                            {preview ? 'Hide previews' : 'Show previews'}
                        </button>
                    </>
                }
            />

            <TCard>
                {failed ? (
                    <TEmpty message="Couldn’t load shares." actionLabel="Retry" onAction={load} />
                ) : shares === null ? (
                    <TSkeleton rows={5} />
                ) : filtered.length === 0 ? (
                    <TEmpty
                        message={filteredOut ? 'No shares match your filters.' : 'No shares yet — they appear here as guests share.'}
                        actionLabel={filteredOut ? 'Clear filters' : undefined}
                        onAction={() => {
                            setSearch('');
                            setStatus('all');
                        }}
                    />
                ) : (
                    filtered.slice(0, limit).map((s) => {
                        const kind = s.type === 'text' ? 'SMS' : `${s.type ?? ''}`.toUpperCase();
                        const when = createdOf(s) ? moment(createdOf(s)).fromNow() : '';
                        return (
                            <div className="trow ev-row" key={s.id}>
                                {preview &&
                                    (s.thumbnailUri ? (
                                        <img className="thumb ev-th" src={s.thumbnailUri} alt="" loading="lazy" />
                                    ) : (
                                        <span className="thumb ev-th" aria-hidden="true" />
                                    ))}
                                <span className="main">
                                    <span className="nm">{s.destination || '—'}</span>
                                    <span className="sub">{[kind, when].filter(Boolean).join(' · ')}</span>
                                </span>
                                <span className={s.sent ? 'ev-st ok' : 'ev-st'}>{s.sent ? 'Sent' : 'Unsent'}</span>
                                <button
                                    type="button"
                                    className="ev-del"
                                    aria-label={`Delete the share to ${s.destination || 'this guest'}`}
                                    title="Delete share"
                                    disabled={deleting.has(s.id)}
                                    onClick={() => setPending(s)}
                                >
                                    <IconClose size={14} />
                                </button>
                            </div>
                        );
                    })
                )}
            </TCard>

            {shares !== null && filtered.length > 0 && (
                <TFoot>
                    <div className="ev-foot">
                        <span>
                            {Math.min(limit, filtered.length).toLocaleString()} of {filtered.length.toLocaleString()}
                            {filteredOut ? ' · filtered' : ''}
                        </span>
                        {filtered.length > limit && (
                            <button type="button" className="tb-btn" onClick={() => setLimit((l) => l + PAGE)}>
                                Show more
                            </button>
                        )}
                    </div>
                </TFoot>
            )}

            <ConfirmDialog
                open={pending !== null}
                title="Delete share?"
                body={`Delete the share to ${pending?.destination || 'this guest'}? This can't be undone.`}
                actionLabel="Delete"
                danger
                onConfirm={() => {
                    if (pending) removeShare(pending);
                }}
                onClose={() => setPending(null)}
            />
            {toast.node}
        </div>
    );
}

export default function SharesV3() {
    return (
        <EventPage section="sharing">
            <SharesBody />
        </EventPage>
    );
}
